// components
import Button from '@sdb/ui/atoms/Button';
import { Icon } from '@sdb/planning-ui';
import DateNavigation from 'components/shared/DateNavigation';
import { HeaderViewOptions } from './components/HeaderViewOptions/HeaderViewOptions';
import AddAvailabilityButton from './components/AddAvailabilityButton/AddAvailabilityButton';
import OpenCalendarButton from './components/OpenCalendarButton/OpenCalendarButton';

// hooks
import { useTranslation } from 'react-i18next';
import { useCallback, useContext } from 'react';
import { useCreateShortcutContext } from 'components/shared/ShortcutProvider';
import { useSelector } from 'hooks';

// stores/context
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useShallow } from 'zustand/shallow';
import { useEntityStore } from 'components/mrp/stores/entityStore/entityStore';
import { CalendarInteractionContext } from '../CalendarContainer/components/CalendarInteractionProvider/CalendarInteractionProvider.context';

// styles
import { ActionsContainer, StyledHeader } from './Header.styles';

// types
import { HeaderProps } from './Header.types';
import { useCalendarNavigationStore } from '../CalendarContainer/components/store/calendarNavigationStore.store';

// consts
import { MrpShortcuts } from 'components/mrp/mrp.consts';

const Header = ({}: HeaderProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const { position } = useCalendarNavigationStore(
    useShallow((state) => ({
      position: state.position,
      event: state.event
    }))
  );

  const anyCalendarsLoading = useEntityStore((state) => state.hasAnyLoading);

  const { appointment } = useAppointmentStore(
    useShallow((state) => ({
      appointment: state.appointment,
      setAppointmentData: state.control.setAppointmentData
    }))
  );

  const hasAddAppointmentAccess = useSelector(
    ({ grants }) => grants.hasAddAppointmentAccess
  );

  const { addAppointment: addAppointmentUtil } = useContext(
    CalendarInteractionContext
  );

  const addAppointment = useCallback(() => {
    if (!position) return;

    addAppointmentUtil(position.calendarId, position.date);
  }, [addAppointmentUtil, position]);

  useCreateShortcutContext('toolbar-actions', [
    {
      name: 'addAppointment',
      label: t('shortcuts.addAppointment.label'),
      description: t('shortcuts.addAppointment.description'),
      callback: addAppointment,
      active: !!position && hasAddAppointmentAccess,
      ...MrpShortcuts.NEW_APPOINTMENT
    }
  ]);

  return (
    <>
      <StyledHeader appointmentFormIsOpen={!appointment}>
        <ActionsContainer start>
          <OpenCalendarButton />

          <Button
            disabled={!!appointment || !position || !hasAddAppointmentAccess}
            variant="outlined"
            startIcon={<Icon type="calendar-plus" />}
            onClick={() => addAppointment()}
          >
            {t('addAppointment')}
          </Button>

          <AddAvailabilityButton disabled={!hasAddAppointmentAccess} />
        </ActionsContainer>

        <ActionsContainer center>
          <DateNavigation
            allowMenuOptions
            defaultPeriod="week"
            options={['day', 'week', 'workweek']}
            disabled={anyCalendarsLoading}
          />

          <HeaderViewOptions />
        </ActionsContainer>
      </StyledHeader>
    </>
  );
};
export default Header;
